import React, { useState } from "react";
import type { AnalyticsData } from "../types/data";
import { Key } from "../constants/enums";
import { columnConfig } from "../constants/config/columns";
import { doNotTruncateKeys } from "../constants/lists";
import { renderHeader } from "../renderers";
import ColumnSortButton from "./header/ColumnSortButton";
import { COLUMN_SORT_KEYS } from "../constants/config/sort";

interface AnalyticsTableProps {
  groups: Array<{ label: string; items: AnalyticsData[] }>;
  headers: Array<{ key: Key; label: string; info: React.ReactNode }>;
  groupBy: Key;
}

const COLUMN_WIDTH: Record<string, string> = {
  [Key.STUDENT_NAME]: "140px",
  [Key.CLASSROOM_NAME]: "202px",
  [Key.LESSON_NAME]: "220px",
  [Key.BITES]: "262px",
  [Key.ASSIGN_DATE]: "112px",
  [Key.TIME_SPENT]: "112px",
  [Key.GOALS]: "112px",
  [Key.PRE_ASSESSMENT]: "148px",
  [Key.EXIT_TICKET]: "132px",
  [Key.CUSTOM_TICKET]: "112px",
};

// A sortable column's header also holds the 24px sort button and its 2px gap.
const SORT_BUTTON_ALLOWANCE = "26px";

const getColumnWidth = (key: Key): string => {
  const width = COLUMN_WIDTH[key] ?? "140px";
  return COLUMN_SORT_KEYS.has(key)
    ? `calc(${width} + ${SORT_BUTTON_ALLOWANCE})`
    : width;
};

const AnalyticsTable = ({ groups, headers, groupBy }: AnalyticsTableProps) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const gridTemplateColumns = headers
    .map((h) => getColumnWidth(h.key))
    .join(" ");

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(groupLabel) ? next.delete(groupLabel) : next.add(groupLabel);
      return next;
    });
  };

  return (
    <div className="et-analytics-table-wrap relative w-full">
      <div
        className="et-analytics-table-scroll w-full font-mulish text-sm overflow-x-auto"
        role="region"
        aria-label="Lesson analytics table; scroll horizontally for all columns"
        tabIndex={0}
      >
        <div
          className="et-analytics-grid grid w-max min-w-full"
          style={
            {
              gridTemplateColumns,
              // The sticky lesson column pins right after the student column.
              "--et-student-column-width": getColumnWidth(Key.STUDENT_NAME),
            } as React.CSSProperties
          }
        >
          <div
            className="et-analytics-table-header grid col-span-full sticky top-0 z-20"
            style={{ gridTemplateColumns: "subgrid" }}
          >
            {headers.map((header) => (
              <div
                key={header.key}
                data-column={header.key}
                className="et-analytics-column-heading flex items-center"
              >
                {renderHeader(
                  header.label,
                  header.info,
                  COLUMN_SORT_KEYS.has(header.key as Key) ? (
                    <ColumnSortButton
                      columnKey={header.key as Key}
                      label={header.label}
                    />
                  ) : null,
                )}
              </div>
            ))}
          </div>
          {groups.map((group, groupIndex) => {
            const previousGroupsHeight = groups
              .slice(0, groupIndex)
              .reduce((sum, g) => {
                const isExpanded = expandedGroups.has(g.label);
                const count = g.items.length;
                const displayed = count > 5 && !isExpanded ? 5 : count;
                return sum + displayed + (count > 5 ? 1 : 0);
              }, 0);

            const groupStartRow = 2 + previousGroupsHeight;
            const isExpanded = expandedGroups.has(group.label);
            const itemCount = group.items.length;
            const showToggle = itemCount > 5;
            const displayedItems =
              showToggle && !isExpanded ? group.items.slice(0, 5) : group.items;
            const totalRowsInGroup =
              displayedItems.length + (showToggle ? 1 : 0);
            const groupBgClass =
              groupIndex % 2 === 0 ? "et-analytics-row-shade" : "";

            return (
              <React.Fragment key={`${group.label}-${groupIndex}`}>
                <div
                  className={`col-span-full ${groupBgClass}`}
                  style={{
                    gridRow: `${groupStartRow} / span ${totalRowsInGroup}`,
                  }}
                />
                {displayedItems.map((item, itemIndex) => {
                  const currentRow = groupStartRow + itemIndex;

                  return (
                    <React.Fragment key={`${group.label}-${itemIndex}`}>
                      {headers.map((header, colIndex) => {
                        const shouldRenderLabel =
                          header.key !== groupBy || itemIndex === 0;
                        const config = columnConfig[header.key];
                        const doNotTruncate = doNotTruncateKeys.includes(
                          header.key,
                        );
                        const cellTitle = config?.getLabel
                          ? config.getLabel(item)
                          : "";

                        return (
                          <div
                            key={`${groupIndex}-${itemIndex}-${header.key}`}
                            className="et-analytics-cell flex relative min-w-0"
                            data-column={header.key}
                            data-group-start={
                              itemIndex === 0 && groupIndex > 0 ? "" : undefined
                            }
                            style={{
                              gridRow: currentRow,
                              gridColumn: colIndex + 1,
                            }}
                            title={
                              shouldRenderLabel && !doNotTruncate && cellTitle
                                ? cellTitle
                                : undefined
                            }
                          >
                            <div className="w-full">
                              {shouldRenderLabel ? config?.render(item) : null}
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {showToggle && (
                  <div
                    className="col-span-full flex justify-center items-center py-2 relative z-20"
                    style={{
                      gridRow: groupStartRow + displayedItems.length,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.label)}
                      className="text-sm font-bold text-[#1C9C6D] px-3 py-1 rounded-md hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1C9C6D]/40 focus-visible:ring-offset-1"
                      aria-expanded={isExpanded}
                      aria-label={
                        isExpanded
                          ? "Show fewer rows"
                          : `Show ${itemCount - 5} more rows`
                      }
                    >
                      {isExpanded ? "Show Less" : `Show ${itemCount - 5} More`}
                    </button>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTable;
